/**
 * Painel lateral (detalhe sem sair da lista, ex.: pedido). Controlado pelo pai.
 * Fecha por backdrop, botão e Escape; o foco vai para o painel ao abrir.
 */
import { createElement as h, useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { X } from "lucide-react";

export interface DrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children?: ReactNode;
}

export function Drawer({ open, title, onClose, children }: DrawerProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return h(
    "div",
    { className: "fixed inset-0 z-50 flex justify-end", "data-drawer": "open" },
    h("button", {
      type: "button",
      "aria-label": "Fechar painel",
      onClick: onClose,
      className: "absolute inset-0 bg-black/40"
    }),
    h(
      "div",
      {
        ref: panelRef,
        role: "dialog",
        "aria-modal": true,
        "aria-label": title,
        tabIndex: -1,
        className: "relative flex h-full w-full max-w-xl flex-col bg-surface shadow-s2 focus-visible:outline-none"
      },
      h(
        "div",
        { className: "flex items-center justify-between gap-3 border-b border-border px-4 py-3" },
        h("h2", { className: "text-lg font-extrabold text-ink" }, title),
        h(
          "button",
          {
            type: "button",
            onClick: onClose,
            "aria-label": "Fechar",
            className:
              "inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-control text-body hover:bg-surface-soft"
          },
          h(X, { className: "h-5 w-5", "aria-hidden": true, focusable: false })
        )
      ),
      h("div", { className: "flex-1 overflow-y-auto p-4" }, children)
    )
  );
}
